import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    /* ── Host ────────────────────────────────────────────────────────────────── */
    :host {
      display: block;

      /*
       * Root cascade tokens.
       * All child elements inherit --_bg and --_color so changing
       * --uwc-panelmenu-bg / --uwc-panelmenu-color cascades everywhere.
       */
      --_bg:    var(--uwc-panelmenu-bg,    var(--uwc-surface, ${t.surface}));
      --_color: var(--uwc-panelmenu-color, var(--uwc-text,    ${t.text}));
    }

    /* ── Scrollable ─────────────────────────────────────────────────────────── */
    .uwc-pm--scrollable {
      overflow-y: auto;
      scrollbar-width: thin;
      scrollbar-color: color-mix(in oklab, var(--_color) 20%, transparent) transparent;
    }
    .uwc-pm--scrollable::-webkit-scrollbar { width: 5px; }
    .uwc-pm--scrollable::-webkit-scrollbar-track { background: transparent; }
    .uwc-pm--scrollable::-webkit-scrollbar-thumb {
      background: color-mix(in oklab, var(--_color) 20%, transparent);
      border-radius: ${t.radiusFull};
    }

    /* ── Container ───────────────────────────────────────────────────────────── */
    .uwc-pm {
      background:    var(--_bg);
      color:         var(--_color);
      border:        var(--uwc-panelmenu-border,  1px solid ${t.border});
      border-radius: var(--uwc-panelmenu-radius,  ${t.radiusMd});
      box-shadow:    var(--uwc-panelmenu-shadow,  none);
      overflow: hidden;
      font-size: var(--uwc-panelmenu-item-font-size, ${t.fontSizeSm});
    }

    /* ─────────────────────────────────────────────────────────────────────────
       PANEL  (accordion unit)
    ───────────────────────────────────────────────────────────────────────── */
    .uwc-pm__panel {
      background: var(--uwc-panelmenu-panel-bg, var(--_bg));
      border-bottom: var(--uwc-panelmenu-panel-border, 1px solid ${t.border});
    }
    .uwc-pm__panel--last {
      border-bottom: none;
    }

    /* ── Panel header button / link ──────────────────────────────────────────── */
    .uwc-pm__header {
      display: flex;
      align-items: center;
      gap: ${t.space3};
      width: 100%;
      padding: var(--uwc-panelmenu-header-padding, ${t.space3} ${t.space4});
      font-size: var(--uwc-panelmenu-header-font-size, ${t.fontSizeMd});
      font-weight: var(--uwc-panelmenu-header-font-weight, ${t.fontWeightSemibold});
      font-family: var(--uwc-font-family, inherit);
      /* Inherits cascade color */
      color: inherit;
      background: transparent;
      border: none;
      cursor: pointer;
      text-align: left;
      text-decoration: none;
      line-height: 1.4;
      transition:
        background ${t.durationFast} ${t.easingStandard},
        color       ${t.durationFast} ${t.easingStandard};
      position: relative;
    }
    .uwc-pm__header:hover {
      background: var(--uwc-panelmenu-header-hover-bg,
        color-mix(in oklab, var(--_color) 6%, transparent));
    }
    .uwc-pm__header:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: -2px;
    }
    .uwc-pm__header--active {
      background: var(--uwc-panelmenu-header-active-bg,
        color-mix(in oklab, var(--_color) 5%, transparent));
      color: var(--uwc-panelmenu-header-active-color, var(--uwc-color-primary, ${t.primary}));
    }
    .uwc-pm__header--disabled {
      opacity: 0.45;
      cursor: not-allowed;
      pointer-events: none;
    }

    /* ── Header sub-elements ─────────────────────────────────────────────────── */
    .uwc-pm__h-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 18px;
      color: inherit;
    }
    .uwc-pm__h-label {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .uwc-pm__h-chevron {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: color-mix(in oklab, currentColor 55%, transparent);
      transition: transform ${t.durationBase} ${t.easingStandard};
      transform: rotate(-90deg); /* points right = collapsed */
    }
    .uwc-pm__h-chevron--open {
      transform: rotate(0deg); /* points down = expanded */
    }

    /* ─────────────────────────────────────────────────────────────────────────
       PANEL BODY  (collapsible wrapper)
       Uses CSS display:grid trick for smooth height animation without
       JavaScript measurement — compatible with all modern browsers.
    ───────────────────────────────────────────────────────────────────────── */
    .uwc-pm__body {
      display: grid;
      grid-template-rows: 0fr;
      overflow: hidden;
      transition: grid-template-rows var(--uwc-panelmenu-duration, ${t.durationslow}) ${t.easingStandard};
    }
    .uwc-pm__body--open {
      grid-template-rows: 1fr;
    }
    .uwc-pm__body-inner {
      overflow: hidden;
    }

    /* ── Items list inside panel body ────────────────────────────────────────── */
    .uwc-pm__items {
      list-style: none;
      margin: 0;
      padding: ${t.space1} 0;
    }

    /* ─────────────────────────────────────────────────────────────────────────
       NESTED ITEM  (button or link)
       --_depth drives the indentation via calc().
    ───────────────────────────────────────────────────────────────────────── */
    .uwc-pm__item {
      display: flex;
      align-items: center;
      gap: ${t.space2};
      width: 100%;
      /* Indent = base-padding + depth × indent-unit */
      padding-inline-start: calc(
        var(--uwc-panelmenu-item-indent-base, ${t.space4})
        + var(--_depth, 0) * var(--uwc-panelmenu-indent, 1rem)
      );
      padding-inline-end: ${t.space4};
      padding-block: var(--uwc-panelmenu-item-padding-y, ${t.space3});
      font-size: var(--uwc-panelmenu-item-font-size, ${t.fontSizeMd});
      font-weight: var(--uwc-panelmenu-item-font-weight, ${t.fontWeightMedium});
      font-family: var(--uwc-font-family, inherit);
      /* All text uses panel color via cascade */
      color: inherit;
      background: transparent;
      border: none;
      cursor: pointer;
      text-align: left;
      text-decoration: none;
      line-height: 1.4;
      transition:
        background ${t.durationFast} ${t.easingStandard},
        color       ${t.durationFast} ${t.easingStandard};
    }
    .uwc-pm__item:hover {
      background: var(--uwc-panelmenu-item-hover-bg,
        color-mix(in oklab, var(--_color) 6%, transparent));
    }
    .uwc-pm__item:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: -2px;
    }
    .uwc-pm__item--expanded {
      color: var(--uwc-panelmenu-item-active-color, var(--uwc-color-primary, ${t.primary}));
      background: var(--uwc-panelmenu-item-active-bg,
        color-mix(in oklab, var(--_color) 4%, transparent));
    }
    .uwc-pm__item--disabled {
      opacity: 0.45;
      cursor: not-allowed;
      pointer-events: none;
    }
    .uwc-pm__item--danger {
      color: var(--uwc-panelmenu-item-danger-color, var(--uwc-color-danger, ${t.danger}));
    }
    .uwc-pm__item--danger:hover {
      background: color-mix(in oklab,
        var(--uwc-panelmenu-item-danger-color, var(--uwc-color-danger, ${t.danger})) 10%,
        transparent);
    }

    /* ── Selected item ───────────────────────────────────────────────────────── */
    .uwc-pm__item--selected {
      background: var(--uwc-panelmenu-item-selected-bg,
        color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 10%, transparent));
      color: var(--uwc-panelmenu-item-selected-color, var(--uwc-color-primary, ${t.primary}));
      font-weight: ${t.fontWeightSemibold};
      /* Inset left border — no position:relative needed */
      box-shadow: inset 3px 0 0 var(--uwc-color-primary, ${t.primary});
    }
    .uwc-pm__item--selected:hover {
      background: var(--uwc-panelmenu-item-selected-bg,
        color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 15%, transparent));
    }

    /* Selected panel header (leaf, no children) */
    .uwc-pm__header--selected {
      background: var(--uwc-panelmenu-item-selected-bg,
        color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 10%, transparent));
      color: var(--uwc-panelmenu-item-selected-color, var(--uwc-color-primary, ${t.primary}));
      box-shadow: inset 3px 0 0 var(--uwc-color-primary, ${t.primary});
    }

    /* ── Item toggler (chevron for sub-items) ────────────────────────────────── */
    .uwc-pm__toggler {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 14px;
      height: 14px;
      color: color-mix(in oklab, currentColor 55%, transparent);
      transition: transform ${t.durationBase} ${t.easingStandard};
      transform: rotate(0deg);
    }
    .uwc-pm__toggler--open {
      transform: rotate(90deg);
    }
    .uwc-pm__toggler-placeholder {
      display: inline-flex;
      flex-shrink: 0;
      width: 14px;
      height: 14px;
    }

    /* ── Item icon ───────────────────────────────────────────────────────────── */
    .uwc-pm__item-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 16px;
      /* Icon follows cascade color */
      color: inherit;
    }

    /* ── Item label ──────────────────────────────────────────────────────────── */
    .uwc-pm__item-label {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* ── Badge ───────────────────────────────────────────────────────────────── */
    .uwc-pm__badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 1.25rem;
      height: 1.25rem;
      padding: 0 ${t.space2};
      font-size: ${t.fontSizeXs};
      font-weight: ${t.fontWeightSemibold};
      border-radius: ${t.radiusFull};
      /* Badge colors are independent of the cascade — intentional highlight */
      background: var(--uwc-panelmenu-badge-bg, var(--uwc-color-primary, ${t.primary}));
      color: var(--uwc-panelmenu-badge-color, #fff);
      flex-shrink: 0;
      line-height: 1;
    }

    /* ── Sub-item accordion (same grid trick as body) ────────────────────────── */
    .uwc-pm__sub {
      display: grid;
      grid-template-rows: 0fr;
      overflow: hidden;
      transition: grid-template-rows var(--uwc-panelmenu-duration, ${t.durationslow}) ${t.easingStandard};
    }
    .uwc-pm__sub--open {
      grid-template-rows: 1fr;
    }
    .uwc-pm__sub-inner {
      overflow: hidden;
    }

    /* ── Separator ───────────────────────────────────────────────────────────── */
    .uwc-pm__separator {
      height: 1px;
      margin: ${t.space1} ${t.space4};
      background: var(--uwc-panelmenu-separator-color,
        color-mix(in oklab, var(--_color) 12%, transparent));
    }

    /* ── Reduced motion ─────────────────────────────────────────────────────── */
    @media (prefers-reduced-motion: reduce) {
      .uwc-pm__body,
      .uwc-pm__sub,
      .uwc-pm__header,
      .uwc-pm__item,
      .uwc-pm__toggler,
      .uwc-pm__h-chevron {
        transition: none;
      }
    }
  `,
];
